import { uploadFileToS3 } from './uploadFileToS3';
import { dataURLtoFile } from './uploadCroppedImage';
import { resizeImageForUpload } from './resizeImageForUpload';
import { TitlePageContent } from '../types/sop';
import { api } from '../api/client';

/**
 * Convert blob URL to File object
 */
const blobUrlToFile = async (blobUrl: string, filename: string): Promise<File> => {
  const response = await fetch(blobUrl);
  const blob = await response.blob();
  return new File([blob], filename, { type: blob.type });
};

export interface ImageExtractionResult {
  originalSrc: string;
  s3Url: string;
  mediaId: string;
}

/**
 * Extracts local images (data URLs) from HTML content and uploads them to S3
 */
export const extractAndUploadTitlePageImages = async (
  htmlContent: string,
  sopId: string
): Promise<{ processedHtml: string; uploadedImages: ImageExtractionResult[] }> => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  
  const images = tempDiv.querySelectorAll('img');
  const localImages = Array.from(images).filter(img => 
    img.src.startsWith('data:image/') || img.src.startsWith('blob:')
  );
  
  if (localImages.length === 0) {
    return { processedHtml: htmlContent, uploadedImages: [] };
  }
  
  const uploadedImages: ImageExtractionResult[] = [];
  const replacements: Record<string, string> = {};
  
  try {
    for (const img of localImages) {
      const imageSrc = img.src;
      
      let file: File;
      if (imageSrc.startsWith('data:image/')) {
        // Convert data URL to File
        file = dataURLtoFile(imageSrc, `title-page-image-${Date.now()}.jpg`);
      } else if (imageSrc.startsWith('blob:')) {
        // Convert blob URL to File
        file = await blobUrlToFile(imageSrc, `title-page-image-${Date.now()}.jpg`);
      } else {
        continue; // Skip non-local images
      }

      file = await resizeImageForUpload(file);

      // Upload to S3
      const uploadResult = await uploadFileToS3(file, sopId);
      
      if (uploadResult && typeof uploadResult !== 'string' && 'media_id' in uploadResult) {
        // Get the actual S3 URL from the backend
        try {
          const downloadRes = await api.post('/download_urls', { 
            image_ids: [uploadResult.media_id] 
          });
          const s3Url = downloadRes.data.urls[uploadResult.media_id];
          
          if (s3Url) {
        replacements[imageSrc] = s3Url;
        
        uploadedImages.push({
          originalSrc: imageSrc,
          s3Url,
          mediaId: uploadResult.media_id
        });
          }
        } catch (error) {
          console.error('Error getting download URL:', error);
          // Fallback to media ID if URL fetch fails
          const fallbackUrl = `/download_urls?media_ids=${uploadResult.media_id}`;
          replacements[imageSrc] = fallbackUrl;
        }
      }
    }
    
    // Replace data URLs with S3 URLs in HTML
    let processedHtml = htmlContent;
    for (const [originalSrc, s3Url] of Object.entries(replacements)) {
      processedHtml = processedHtml.replace(new RegExp(originalSrc.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), s3Url);
    }
    
    return { processedHtml, uploadedImages };
  } catch (error) {
    console.error('Error uploading title page images:', error);
    // Return original content if upload fails
    return { processedHtml: htmlContent, uploadedImages: [] };
  }
};

/**
 * Injects cover image into details HTML when backend has coverImage but details has no img.
 */
export const injectCoverImageIntoDetails = (
  details: string,
  coverImageUrl: string,
): string => {
  if (!coverImageUrl?.trim()) return details;
  if (details.includes(coverImageUrl)) return details;
  const escaped = coverImageUrl.replace(/"/g, '&quot;');
  return `<p style="text-align: center"><img src="${escaped}" alt="Cover" /></p>${details}`;
};

/**
 * Extracts cover image URL from title page content
 */
export const extractCoverImageUrl = (htmlContent: string): string | undefined => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;

  const images = tempDiv.querySelectorAll('img');
  const coverImage = Array.from(images).find(
    (img) =>
      !img.hasAttribute('data-avatar') &&
      !img.closest('[data-column]'),
  );

  return coverImage?.src;
};

/**
 * Extracts authored by information from title page content
 */
export const extractAuthoredByInfo = (htmlContent: string) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  
  const layoutContainers = tempDiv.querySelectorAll('[data-layout-type="column"]');
  
  if (layoutContainers.length === 0) {
    return undefined;
  }
  
  const authoredBy: TitlePageContent['authoredBy'] = {
    created: {
      name: '',
      avatar: '',
      date: ''
    },
    updated: {
      name: '',
      avatar: '',
      date: ''
    }
  };
  
  layoutContainers.forEach((container) => {
    const paragraphs = container.querySelectorAll('p[data-column]');
    
    paragraphs.forEach((paragraph) => {
      const column = paragraph.getAttribute('data-column');
      const text = paragraph.textContent || '';
      
      if (text.includes('Created by') && column === '1') {
        const avatar = paragraph.querySelector('img[data-avatar="true"]') as HTMLImageElement | null;
        const nameMatch = text.match(/Username/);
        

        const readonlyTextSpan = paragraph.querySelector('span[data-readonly-text="true"]');
        const italicText = paragraph.querySelector('em');
        const extractedDate = readonlyTextSpan?.textContent || italicText?.textContent || text.match(/\d+\/\d+\/\d+, \d+:\d+:\d+ [AP]M/)?.[0] || new Date().toLocaleString();
        
        authoredBy.created = {
          name: nameMatch?.[0] || 'Username',
          avatar: avatar?.src || '/api/placeholder/32/32',
          date: extractedDate
        };
      } else if (text.includes('Updated by') && column === '2') {
        const avatar = paragraph.querySelector('img[data-avatar="true"]') as HTMLImageElement | null;
        const nameMatch = text.match(/Username/);

        const readonlyTextSpan = paragraph.querySelector('span[data-readonly-text="true"]');
        const italicText = paragraph.querySelector('em');
        const extractedDate = readonlyTextSpan?.textContent || italicText?.textContent || text.match(/\d+\/\d+\/\d+, \d+:\d+:\d+ [AP]M/)?.[0] || new Date().toLocaleString();
        
        authoredBy.updated = {
          name: nameMatch?.[0] || 'Username',
          avatar: avatar?.src || '/api/placeholder/32/32',
          date: extractedDate
        };
      }
    });
  });
  
  return Object.keys(authoredBy).length > 0 ? authoredBy : undefined;
};

/**
 * Replaces the cover image placeholder div with an inert div so that when
 * viewing a done version, the interactive "Add a cover image" node is never
 * instantiated. Call this only when loading content for read-only (done) versions.
 */
export function replaceCoverPlaceholderForReadOnly(html: string): string {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  const placeholder = tempDiv.querySelector(
    'div[data-image-placeholder="true"][data-placeholder-id^="cover-placeholder-"]',
  );
  if (!placeholder) return html;
  const inert = document.createElement('div');
  inert.className = 'cover-placeholder-readonly';
  inert.style.minHeight = '120px';
  placeholder.replaceWith(inert);
  return tempDiv.innerHTML;
}
